import { atom, atomFamily } from 'recoil';
import type { RecoilState } from 'recoil';
import {
  broadcastChannelSynchronizerEffect,
  localForagePersisterEffect,
} from 'utils/recoilEffects';

/**
 * Normalizes a reporter pathname to the stable base reporter route used as the
 * `recorderReadinessAtom` key.
 *
 * The reporter page route is `/case/:worklistId/reporter`, but the window can
 * navigate to nested subroutes (e.g. `/case/:id/reporter/macro/...`). Readiness
 * is per-reporter-page, so all readers/writers must agree on a single key.
 * We key off the path up to and including `/reporter` — this stays stable across
 * in-reporter navigation while preserving the `:worklistId` (which precedes
 * `/reporter`), so different cases remain distinct. Returns `null` unchanged so
 * "no reporter tab open" keeps its own key.
 */
export const getRecorderReadinessKey = (pathname: string | null): string | null => {
  if (pathname == null) return null;
  const marker = '/reporter';
  const markerIndex = pathname.indexOf(marker);
  if (markerIndex === -1) return pathname;
  return pathname.slice(0, markerIndex + marker.length);
};

/**
 * Tracks whether the recorder in the reporter window is initialized and has an
 * active media stream — i.e. ready to record.
 *
 * Key: the normalized base reporter route (see `getRecorderReadinessKey`, e.g.
 * `/case/:worklistId/reporter`). All call sites must derive the key through that
 * helper so readers and writers stay in sync across in-reporter navigation.
 * Use `null` when no reporter tab is open; the atom defaults to `false`.
 *
 * Writer: `RecorderProviderBase` (useRecorder) — sets `true` once
 *   `isRecorderInitialized && streamRef.current != null`, resets to `false` on
 *   unmount.
 * Readers: `Recorder` (reporter window) gates the record button;
 *   `ViewerRecordButton` (viewer window) gates the cross-tab record button and
 *   drives the "Start Read" loading state via `useReporterOpeningState`.
 *
 * Cross-window sync is handled by `broadcastChannelSynchronizerEffect` with
 * the reporter as conductor.
 */
export const recorderReadinessAtom = atomFamily<boolean, string | null>({
  key: 'recorderReadiness',
  default: false,
  effects: [broadcastChannelSynchronizerEffect({ conductor: 'reporter' })],
});

export const recorderState: RecoilState<boolean> = atom({
  key: 'recorderState',
  default: false,
  effects: [broadcastChannelSynchronizerEffect()],
});

/**
 * Tracks whether user has started recording for a given case.
 *
 * Cross-window sync: Recording starts in the reporter window, but the viewer
 * window needs to know if recording has happened. This is used as a proxy for
 * when the user is 'done' hanging their study the way they like it.
 */
export const hasRecordedForCaseAtom = atomFamily<boolean, string>({
  key: 'hasRecordedForCaseAtom',
  default: false,
  effects: [
    broadcastChannelSynchronizerEffect({ conductor: 'reporter' }),
    localForagePersisterEffect({
      maxKeys: 250,
    }),
  ],
});

/**
 * Tracks whether any viewer has initiated the "Start Read" flow and is waiting
 * for the reporter to open and the recorder to become ready.
 *
 * Stored as Recoil state (not local React state) so that all open viewer tabs
 * see the loading state when any one of them clicks "Start Read".
 *
 * Writer: `useReporterOpeningState` — set `true` on click, reset `false` once
 *   the recorder is ready or the reporter closes.
 */
export const isOpeningReporterAtom = atom<boolean>({
  key: 'isOpeningReporter',
  default: false,
  effects: [broadcastChannelSynchronizerEffect()],
});
